import {Injectable} from '@angular/core';
import {ParentBasedMultiselectProvider} from '../../multiselect-provider';
import {Observable, Observer} from 'rxjs';
import {MultiselectOptionItem, UiConstants} from '../../../util/core-utils';
import {QueryResult} from '../../util/services';
import {TaskCustomStatistics, TaskCustomStatisticsService} from './task-custom-statistics.service';
import {List} from 'immutable';
import {FilterField} from '../../query/filterfields';
import {CriteriaBuilder} from '../../../util/model-utils';
import {Query} from '../../query/field';
import {CustomStatistics} from "../../custom-statistics/custom-statistics";

@Injectable()
export class TaskCustomStatisticsMultiselectProvider implements ParentBasedMultiselectProvider {

  private multiselectQueryRequest: CustomStatistics.QueryRequest = {
    fields: f => f.multiselect,
    order: f => List.of(f.name.asc().nullsLast()),
    paging: {
      pageNumber: 1,
      numberOfItems: UiConstants.autocompletePageSize,
    },
    noProgressBar: true,
  };

  constructor(private taskCustomStatisticsService: TaskCustomStatisticsService) {
  }

  load(queryRequest: TaskCustomStatistics.QueryRequest): Observable<MultiselectOptionItem<number>[]> {
    return Observable.create((observer: Observer<MultiselectOptionItem<number>[]>) => {
      this.taskCustomStatisticsService.query(queryRequest).subscribe((result: QueryResult<CustomStatistics.CustomStatistics>) => {
          observer.next(result.items.map(item => this.toMultiselectOptionItem(item!)).toArray());
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  loadAll(taskId: number, predicate?: string): Observable<MultiselectOptionItem<number>[]> {
    const queryRequest = {...Object.assign({}, this.multiselectQueryRequest), taskId: taskId};
    queryRequest.filter = (f: FilterField.CustomStatistics) => CriteriaBuilder.builder()
      .addString((predicate) => f.name.containsIgnoreCase(predicate), predicate)
      .build();
    return this.load(queryRequest);
  }

  getById(taskId: number, id: number): Observable<MultiselectOptionItem<number>> {
    return Observable.create((observer: Observer<MultiselectOptionItem<number>>) => {
      this.taskCustomStatisticsService.get({
        taskId: taskId,
        id: id,
        fields: f => f.multiselect
      }).subscribe((result: CustomStatistics.CustomStatistics) => {
          observer.next(this.toMultiselectOptionItem(result!));
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  getByIds(taskId: number, ids: number[]): Observable<MultiselectOptionItem<number>[]> {
    const queryRequest = {...Object.assign({}, this.multiselectQueryRequest), taskId: taskId};
    const filters: Query.Criteria[] = [];
    const f: FilterField.CustomStatistics = new FilterField.CustomStatistics();
    ids.forEach(id => {
      filters.push(f.id.eq(id));
    });
    queryRequest.filter = f => Query.Criterias.anyOf(List.of(...filters));
    return this.load(queryRequest);
  }

  toMultiselectOptionItem(item: CustomStatistics.CustomStatistics): MultiselectOptionItem<number> {
    return TaskCustomStatisticsMultiselectProvider.toPublic(item);
  }

  static toPublic(item: CustomStatistics.CustomStatistics): MultiselectOptionItem<number> {
    const ret = new MultiselectOptionItem<number>();
    ret.id = item.id;
    ret.itemName = item.name!;
    ret.itemSubtitle = item.externalId;
    return ret;
  }
}
